import React from 'react';
import css from './ListingPage.module.css';
import { Heading } from '../../components';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney, roundUpToNearestWholeNumber, roundUpToNearestPercentage } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import PropTypes from 'prop-types';
import { Divider, Paper } from '@mui/material';

const { Money } = sdkTypes;

const mapPetType = (petType) => {
  switch (petType.toLowerCase()) {
    case 'dogs':
      return 'Dog';
    case 'cats':
      return 'Cat';
    case 'puppies':
      return 'Puppy';
    default:
      return petType;
  }
};

const SectionPetPricing = ({ publicData, intl }) => {
  const { petPricing, sittingtypes, sittingservices } = publicData;

  const sittingTypes = sittingtypes || [];
  const sittingServices = sittingservices || [];

  if (!petPricing || sittingTypes.length === 0) {
    return null;
  }

  const renderPriceItem = (petType, keyPrefix) => {
    const petData = petPricing[`${keyPrefix}${petType}`];

    if (!petData || !petData.basePrice) {
      return null;
    }

    const basePrice = new Money(roundUpToNearestWholeNumber(petData.basePrice.amount / 0.80), petData.basePrice.currency);
    const formattedBasePrice = formatMoney(intl, basePrice);

    const additionalValue = parseFloat(petData?.additionalPetValue || 0) / 0.80;
    const additionalType = petData?.additionalPetType;

    let additionalText = '';
    if (additionalValue) {
      if (additionalType === 'percentage') {
        additionalText = ` +${roundUpToNearestPercentage(additionalValue)}% per additional ${petType.toLowerCase()}`;
      } else if (additionalType === 'fixed') {
        const additionalPrice = new Money(roundUpToNearestWholeNumber(Number(additionalValue) * 100), petData.basePrice.currency);
        const formattedAdditionalPrice = formatMoney(intl, additionalPrice);
        additionalText = ` +${formattedAdditionalPrice} per additional ${petType.toLowerCase()}`;
      }
    }

    let inOfficeText = null;
    if (sittingServices.includes('inofficecoverage') && parseInt(petData?.baseInOfficePrice?.amount || 0, 10) > 0) {
      let additionalInOfficeText = '';
      const additionalInOfficeValue = parseFloat(petData?.additionalInOfficePetValue || 0) / 0.80;
      const additionalInOfficeType = petData?.additionalInOfficePetType;
      if (additionalInOfficeValue) {
        if (additionalInOfficeType === 'percentage') {
          additionalInOfficeText = ` +${roundUpToNearestPercentage(additionalInOfficeValue)}% per additional ${petType.toLowerCase()}`;
        } else if (additionalInOfficeType === 'fixed') {
          const additionalInOfficePrice = new Money(roundUpToNearestWholeNumber(Number(additionalInOfficeValue) * 100), petData.basePrice.currency);
          const formattedAdditionalInOfficePrice = formatMoney(intl, additionalInOfficePrice);
          additionalInOfficeText = ` +${formattedAdditionalInOfficePrice} per additional ${petType.toLowerCase()}`;
        }
      }

      const baseInOfficePrice = new Money(roundUpToNearestWholeNumber(petData.baseInOfficePrice.amount / 0.80), petData.baseInOfficePrice.currency);
      const formattedBaseInOfficePrice = formatMoney(intl, baseInOfficePrice);
      inOfficeText = (
        <div className={css.pricingRow} key={`${keyPrefix}-${petType}`}>
          <span className={css.detailLabel}>
            {keyPrefix === 'standard' ? 'Standard In Office Coverage:' : 'Holiday In Office Coverage:'}
          </span>{' '}
          {formattedBaseInOfficePrice} each{additionalInOfficeText}
        </div>
      );
    }

    return (
      <>
        <div className={css.pricingRow} key={`${keyPrefix}-${petType}`}>
          <span className={css.detailLabel}>
            {keyPrefix === 'standard' ? 'Standard:' : 'Holiday:'}
          </span>{' '}
          {formattedBasePrice} each{additionalText}
        </div>
        {inOfficeText}
      </>
    );
  };

  return (
    <section className={css.sectionDetails}>
      <Paper className={css.pricingPaper} elevation={2}>
        <div className={css.prices}>
          {sittingTypes.map((petType, index) => {
            const mappedType = mapPetType(petType);
            const headingText = mappedType === 'Puppy' ? 'Puppies' : `${mappedType}s`;

            return (
              <div key={mappedType} className={css.pricingBlock}>
                <Heading as="h2" rootClassName={css.sectionHeading}>
                  {headingText}
                </Heading>
                
                {renderPriceItem(mappedType, 'standard')}
                {renderPriceItem(mappedType, 'holiday')}

                {
                  index < sittingTypes.length - 1
                  && (
                    <Divider className={css.divider} />
                  )
                }
              </div>
            );
          })}

          <p className={css.details}>
            <FormattedMessage id="ListingPage.petPricing.details" />
          </p>
        </div>
      </Paper>
    </section>
  );
};

SectionPetPricing.propTypes = {
  publicData: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
};

export default SectionPetPricing;